/* ==========================================================================
   ZYREX — assets/predictive-search.js
   <predictive-search> — live results under the search field.

   Loaded after assets/theme.js, so window.Zyrex and window.ZUtils are already
   there; every use of them is still guarded, because one missing global must
   never take the search form itself down. With this file blocked the form is
   an ordinary GET to /search and everything still works.

   WHAT IT DOES
     · 250ms debounce, one in-flight request, aborted the moment a newer
       keystroke arrives
     · asks routes.predictive_search_url for resources[type] and
       resources[limit]; when the host element names a section with
       data-section-id it asks for that section's HTML instead of JSON
     · skeleton rows while a request is out
     · full combobox semantics — aria-expanded, aria-controls,
       aria-activedescendant, role="listbox" and role="option"
     · Up, Down, Home, End, Enter, Escape and Tab
     · an empty state, an error state, recent searches kept in localStorage
       and a view-all row that carries the query to the search page

   EXPECTED MARKUP (see sections/header.liquid)
     <predictive-search data-limit data-types data-min-chars data-enabled
                        data-section-id data-label-recent data-label-clear>
       <form data-predictive-form>
         <input data-predictive-input name="q">
         <button data-predictive-reset hidden>
       </form>
       <div data-predictive-status role="status" aria-live="polite"></div>
       <div data-predictive-panel hidden></div>
       <div data-predictive-default>
         <div data-predictive-recent hidden></div>
         … popular searches, rendered by Liquid …
       </div>
     </predictive-search>
   ========================================================================== */

(function () {
  'use strict';

  var win = window;
  var doc = document;

  /* ---- Constants -------------------------------------------------------- */

  var DEBOUNCE_MS = 250;
  var DEFAULT_LIMIT = 6;
  var DEFAULT_MIN_CHARS = 2;
  var DEFAULT_TYPES = 'product,collection,page,article';
  var RECENT_KEY = 'zyrex:recent-searches';
  var RECENT_MAX = 6;
  var SKELETON_ROWS = 4;

  /* The order results are shown in, and the strings key each group uses. */
  var GROUPS = [
    { key: 'products', type: 'product', label: 'products' },
    { key: 'collections', type: 'collection', label: 'collections' },
    { key: 'pages', type: 'page', label: 'pages' },
    { key: 'articles', type: 'article', label: 'articles' },
    { key: 'queries', type: 'query', label: 'suggestions' }
  ];

  var uid = 0;

  /* ---- Small helpers ---------------------------------------------------- */

  function isFn(value) {
    return typeof value === 'function';
  }

  function qs(selector, scope) {
    return (scope || doc).querySelector(selector);
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isFinite(parsed) ? parsed : fallback;
  }

  function utils() {
    return win.ZUtils || null;
  }

  function config() {
    return win.Zyrex || {};
  }

  /** A translated UI string, with a plain English fallback. */
  function string(group, key, fallback) {
    var all = config().strings;
    if (all && all[group] && all[group][key]) return all[group][key];
    return fallback;
  }

  function setting(key, fallback) {
    var settings = config().settings;
    if (!settings || settings[key] === undefined || settings[key] === null) return fallback;
    return settings[key];
  }

  function route(key, fallback) {
    var routes = config().routes;
    if (!routes || !routes[key]) return fallback;
    return routes[key];
  }

  /** Trailing-edge debounce with a cancel, so a disconnect drops the call. */
  function debounce(fn, wait) {
    var helpers = utils();
    if (helpers && isFn(helpers.debounce)) return helpers.debounce(fn, wait);

    var timer = null;
    function debounced() {
      var context = this;
      var args = arguments;
      if (timer) win.clearTimeout(timer);
      timer = win.setTimeout(function () {
        timer = null;
        fn.apply(context, args);
      }, wait);
    }
    debounced.cancel = function () {
      if (timer) win.clearTimeout(timer);
      timer = null;
    };
    return debounced;
  }

  function announce(message) {
    var helpers = utils();
    if (helpers && isFn(helpers.announce)) helpers.announce(message);
  }

  function element(tag, className, text) {
    var node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null && text !== '') node.textContent = String(text);
    return node;
  }

  function empty(node) {
    if (!node) return;
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /* ---- Money ------------------------------------------------------------
     The predictive endpoint answers with a decimal string in the shop's
     currency ("25.00"). ZUtils.formatMoney strips the separator and treats
     what is left as cents, which lands on the right number either way. A
     value that already carries a symbol is passed straight through. */

  function money(value) {
    if (value === undefined || value === null || value === '') return '';
    if (typeof value === 'string' && /[^\d.,\s-]/.test(value)) return value;
    var helpers = utils();
    if (helpers && isFn(helpers.formatMoney)) return helpers.formatMoney(value);
    return String(value);
  }

  function amount(value) {
    if (value === undefined || value === null || value === '') return NaN;
    return parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
  }

  /* ---- Image sizing ------------------------------------------------------ */

  function thumbUrl(url, width) {
    if (!url) return '';
    var clean = String(url);
    if (clean.indexOf('width=') !== -1) return clean;
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ---- URLs -------------------------------------------------------------- */

  function suggestBase() {
    var base = route('predictiveSearch', '/search/suggest');
    var mark = base.indexOf('?');
    return mark === -1 ? base : base.slice(0, mark);
  }

  function searchBase() {
    var base = route('search', '/search');
    var mark = base.indexOf('?');
    return mark === -1 ? base : base.slice(0, mark);
  }

  function searchUrl(query) {
    return (
      searchBase() +
      '?q=' +
      encodeURIComponent(query) +
      '&type=product,article,page&options%5Bprefix%5D=last'
    );
  }

  /* ---- Recent searches --------------------------------------------------- */

  function readRecent() {
    try {
      var raw = win.localStorage.getItem(RECENT_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.length) return [];
      var out = [];
      for (var i = 0; i < parsed.length && out.length < RECENT_MAX; i += 1) {
        if (typeof parsed[i] === 'string' && parsed[i].trim()) out.push(parsed[i].trim());
      }
      return out;
    } catch (error) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      win.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (error) {
      /* storage off or full — recent searches are a nicety, not a feature */
    }
  }

  function rememberQuery(query) {
    var term = String(query || '').trim();
    if (term.length < 2) return;
    var list = readRecent();
    var next = [term];
    for (var i = 0; i < list.length; i += 1) {
      if (list[i].toLowerCase() !== term.toLowerCase()) next.push(list[i]);
    }
    writeRecent(next);
  }

  function forgetAll() {
    try {
      win.localStorage.removeItem(RECENT_KEY);
    } catch (error) {
      /* as above */
    }
  }

  /* ======================================================================
     <predictive-search>
     ====================================================================== */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.form = qs('[data-predictive-form]', this) || qs('form', this);
      this.input = qs('[data-predictive-input]', this) || qs('input[type="search"]', this);
      if (!this.input) return;

      this.panel = qs('[data-predictive-panel]', this);
      this.defaultPanel = qs('[data-predictive-default]', this);
      this.recentHost = qs('[data-predictive-recent]', this);
      this.resetButton = qs('[data-predictive-reset]', this);
      this.status = qs('[data-predictive-status]', this);

      uid += 1;
      this.uid = 'PredictiveSearch-' + uid;

      this.limit = toInt(this.getAttribute('data-limit'), DEFAULT_LIMIT);
      this.minChars = toInt(this.getAttribute('data-min-chars'), DEFAULT_MIN_CHARS);
      this.types = this.getAttribute('data-types') || DEFAULT_TYPES;
      this.sectionId = this.getAttribute('data-section-id') || '';
      this.enabled =
        this.getAttribute('data-enabled') !== 'false' && setting('predictiveSearch', true) !== false;

      this.recentLabel = this.getAttribute('data-label-recent') || 'Recent searches';
      this.clearLabel =
        this.getAttribute('data-label-clear') || string('filters', 'clear', 'Clear');

      this.options = [];
      this.activeIndex = -1;
      this.isOpen = false;
      this.controller = null;
      this.lastQuery = '';

      this.setupAria();
      this.bind();
      this.renderRecent();
      this.syncResetButton();
    }

    disconnectedCallback() {
      if (this.debounced && isFn(this.debounced.cancel)) this.debounced.cancel();
      this.abort();
      this.unbind();
    }

    /* ---- Wiring --------------------------------------------------------- */

    setupAria() {
      if (!this.panel) return;

      if (!this.panel.id) this.panel.id = this.uid + '-listbox';
      this.panel.setAttribute('role', 'listbox');
      this.panel.setAttribute(
        'aria-label',
        string('search', 'suggestions', 'Suggestions')
      );

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.panel.id);
    }

    bind() {
      var self = this;

      this.debounced = debounce(function () {
        self.search(self.input.value);
      }, DEBOUNCE_MS);

      this.onInput = function () {
        self.syncResetButton();
        var value = self.input.value.trim();

        if (value.length < self.minChars) {
          if (self.debounced && isFn(self.debounced.cancel)) self.debounced.cancel();
          self.abort();
          self.close();
          self.showDefault(true);
          return;
        }

        if (!self.enabled) return;
        self.showDefault(false);
        self.debounced();
      };

      this.onFocus = function () {
        if (self.input.value.trim().length < self.minChars) {
          self.renderRecent();
          self.showDefault(true);
        } else if (self.options.length) {
          self.open();
        }
      };

      this.onKeydown = function (event) {
        self.handleKeydown(event);
      };

      this.onSubmit = function () {
        rememberQuery(self.input.value);
      };

      this.onReset = function (event) {
        event.preventDefault();
        self.input.value = '';
        self.syncResetButton();
        self.abort();
        self.close();
        self.renderRecent();
        self.showDefault(true);
        self.input.focus();
      };

      this.onDocumentClick = function (event) {
        if (!self.isOpen) return;
        if (self.contains(event.target)) return;
        self.close();
      };

      this.onPanelClick = function (event) {
        var option = event.target && event.target.closest ? event.target.closest('[role="option"]') : null;
        if (!option) return;
        rememberQuery(self.input.value);
      };

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.input.addEventListener('keydown', this.onKeydown);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      if (this.resetButton) this.resetButton.addEventListener('click', this.onReset);
      if (this.panel) this.panel.addEventListener('click', this.onPanelClick);
      doc.addEventListener('click', this.onDocumentClick);
    }

    unbind() {
      if (this.input) {
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('focus', this.onFocus);
        this.input.removeEventListener('keydown', this.onKeydown);
      }
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.resetButton) this.resetButton.removeEventListener('click', this.onReset);
      if (this.panel) this.panel.removeEventListener('click', this.onPanelClick);
      doc.removeEventListener('click', this.onDocumentClick);
    }

    syncResetButton() {
      if (!this.resetButton) return;
      if (this.input.value.trim()) this.resetButton.removeAttribute('hidden');
      else this.resetButton.setAttribute('hidden', '');
    }

    showDefault(show) {
      if (!this.defaultPanel) return;
      if (show) this.defaultPanel.removeAttribute('hidden');
      else this.defaultPanel.setAttribute('hidden', '');
    }

    /* ---- Keyboard -------------------------------------------------------- */

    handleKeydown(event) {
      var key = event.key;

      if (key === 'Escape' || key === 'Esc') {
        if (!this.isOpen) return;
        // Closes the list wherever this element is used on its own — the
        // search page, say. Inside the header overlay <modal-dialog> listens
        // on the document in the capture phase and gets there first, so one
        // Escape closes the whole sheet, which is what a visitor expects of
        // a panel that covers the top of the page.
        this.close();
        this.showDefault(true);
        return;
      }

      if (key === 'ArrowDown' || key === 'Down') {
        if (!this.options.length) return;
        event.preventDefault();
        this.move(this.activeIndex + 1);
        return;
      }

      if (key === 'ArrowUp' || key === 'Up') {
        if (!this.options.length) return;
        event.preventDefault();
        this.move(this.activeIndex - 1);
        return;
      }

      if (key === 'Home' && this.isOpen && this.options.length) {
        event.preventDefault();
        this.move(0);
        return;
      }

      if (key === 'End' && this.isOpen && this.options.length) {
        event.preventDefault();
        this.move(this.options.length - 1);
        return;
      }

      if (key === 'Enter') {
        var active = this.options[this.activeIndex];
        if (!active) {
          rememberQuery(this.input.value);
          return;
        }
        event.preventDefault();
        rememberQuery(this.input.value);
        if (active.href) win.location.assign(active.href);
        else active.click();
        return;
      }

      if (key === 'Tab') this.close();
    }

    move(index) {
      if (!this.options.length) return;
      var total = this.options.length;
      var next = index;
      if (next < 0) next = total - 1;
      if (next >= total) next = 0;

      for (var i = 0; i < total; i += 1) {
        var option = this.options[i];
        var active = i === next;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
      }

      this.activeIndex = next;
      var current = this.options[next];
      if (current) {
        this.input.setAttribute('aria-activedescendant', current.id);
        if (isFn(current.scrollIntoView)) {
          current.scrollIntoView({ block: 'nearest' });
        }
      }
    }

    clearActive() {
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      for (var i = 0; i < this.options.length; i += 1) {
        this.options[i].classList.remove('is-active');
        this.options[i].setAttribute('aria-selected', 'false');
      }
    }

    /* ---- Open and close --------------------------------------------------- */

    open() {
      if (!this.panel) return;
      this.panel.removeAttribute('hidden');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }

    close() {
      if (!this.panel) return;
      this.panel.setAttribute('hidden', '');
      this.input.setAttribute('aria-expanded', 'false');
      this.isOpen = false;
      this.clearActive();
      this.options = [];
    }

    /* ---- Fetching --------------------------------------------------------- */

    abort() {
      if (this.controller && isFn(this.controller.abort)) {
        try {
          this.controller.abort();
        } catch (error) {
          /* an already-settled request */
        }
      }
      this.controller = null;
    }

    requestUrl(query) {
      var params = [
        'q=' + encodeURIComponent(query),
        'resources%5Btype%5D=' + encodeURIComponent(this.types),
        'resources%5Blimit%5D=' + encodeURIComponent(String(this.limit)),
        'resources%5Boptions%5D%5Bunavailable_products%5D=last'
      ];
      if (this.sectionId) params.push('section_id=' + encodeURIComponent(this.sectionId));

      var base = suggestBase();
      if (!this.sectionId && !/\.json$/.test(base)) base += '.json';

      return base + '?' + params.join('&');
    }

    search(rawQuery) {
      var query = String(rawQuery || '').trim();
      if (query.length < this.minChars) {
        this.close();
        this.showDefault(true);
        return;
      }
      if (!this.enabled || !isFn(win.fetch)) return;

      this.lastQuery = query;
      this.abort();
      this.renderLoading();

      var self = this;
      var signal = null;
      if (win.AbortController) {
        this.controller = new win.AbortController();
        signal = this.controller.signal;
      }

      var options = { method: 'GET', headers: {} };
      options.headers.Accept = this.sectionId ? 'text/html' : 'application/json';
      options.headers['X-Requested-With'] = 'XMLHttpRequest';
      if (signal) options.signal = signal;

      win
        .fetch(this.requestUrl(query), options)
        .then(function (response) {
          if (!response.ok) throw new Error('Search failed with ' + response.status);
          return self.sectionId ? response.text() : response.json();
        })
        .then(function (payload) {
          if (self.lastQuery !== query) return;
          if (self.sectionId) self.renderSection(payload, query);
          else self.renderResults(payload, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (self.lastQuery !== query) return;
          self.renderError();
        });
    }

    /* ---- Rendering --------------------------------------------------------- */

    renderLoading() {
      if (!this.panel) return;
      empty(this.panel);
      this.options = [];
      this.clearActive();

      var wrap = element('div', 'predictive__groups');
      var group = element('div', 'predictive__group');

      for (var i = 0; i < SKELETON_ROWS; i += 1) {
        var row = element('div', 'predictive__skeleton-row');
        row.appendChild(element('span', 'skeleton predictive__skeleton-thumb'));
        var lines = element('span', 'predictive__skeleton-lines');
        lines.appendChild(element('span', 'skeleton predictive__skeleton-line'));
        lines.appendChild(
          element('span', 'skeleton predictive__skeleton-line predictive__skeleton-line--short')
        );
        row.appendChild(lines);
        group.appendChild(row);
      }

      wrap.appendChild(group);
      this.panel.appendChild(wrap);
      this.panel.setAttribute('aria-busy', 'true');
      this.open();
      this.setStatus(string('a11y', 'loading', 'Loading'));
    }

    renderError() {
      if (!this.panel) return;
      empty(this.panel);
      this.options = [];
      this.clearActive();
      this.panel.removeAttribute('aria-busy');

      var box = element('div', 'predictive__empty');
      box.appendChild(
        element('p', 'predictive__empty-title', string('errors', 'title', 'Something went wrong'))
      );
      box.appendChild(
        element(
          'p',
          'predictive__empty-text',
          string('errors', 'generic', 'Please try again in a moment.')
        )
      );
      this.panel.appendChild(box);
      this.open();
      this.setStatus(string('errors', 'generic', 'Please try again in a moment.'));
    }

    /** Section Rendering mode: the server already drew the results. */
    renderSection(html, query) {
      if (!this.panel) return;
      var parsed = null;
      try {
        parsed = new DOMParser().parseFromString(html, 'text/html');
      } catch (error) {
        this.renderError();
        return;
      }

      var incoming =
        parsed.querySelector('[data-predictive-results]') ||
        parsed.querySelector('.shopify-section') ||
        parsed.body;

      empty(this.panel);
      this.panel.removeAttribute('aria-busy');
      while (incoming.firstChild) this.panel.appendChild(incoming.firstChild);

      this.appendViewAll(this.panel, query);
      this.collectOptions();

      this.open();
      this.announceCount(this.options.length, query);
    }

    /** JSON mode: build every row here so nothing is ever injected as HTML. */
    renderResults(payload, query) {
      if (!this.panel) return;

      var results =
        payload && payload.resources && payload.resources.results ? payload.resources.results : {};

      empty(this.panel);
      this.options = [];
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.panel.removeAttribute('aria-busy');

      var total = 0;
      var wrap = element('div', 'predictive__groups');

      for (var g = 0; g < GROUPS.length; g += 1) {
        var meta = GROUPS[g];
        var items = results[meta.key];
        if (!items || !items.length) continue;

        var group = element('div', 'predictive__group');
        group.setAttribute('role', 'group');

        var title = string('search', meta.label, meta.key);
        group.setAttribute('aria-label', title);

        var heading = element('p', 'predictive__group-title', title);
        heading.setAttribute('aria-hidden', 'true');
        group.appendChild(heading);

        for (var i = 0; i < items.length && i < this.limit; i += 1) {
          var option = this.buildOption(items[i], meta, total);
          if (!option) continue;
          group.appendChild(option);
          total += 1;
        }

        wrap.appendChild(group);
      }

      this.panel.appendChild(wrap);

      if (total === 0) {
        this.renderEmpty(query);
        return;
      }

      this.appendViewAll(this.panel, query);
      this.collectOptions();
      this.open();
      this.announceCount(total, query);
    }

    renderEmpty(query) {
      if (!this.panel) return;
      empty(this.panel);
      this.options = [];
      this.clearActive();

      var box = element('div', 'predictive__empty');
      box.appendChild(
        element(
          'p',
          'predictive__empty-title',
          string('search', 'noResults', 'No results found')
        )
      );
      box.appendChild(
        element(
          'p',
          'predictive__empty-text',
          string('search', 'resultsFor', 'Results for') + ' “' + query + '”'
        )
      );
      this.panel.appendChild(box);

      this.appendViewAll(this.panel, query);
      this.collectOptions();
      this.open();
      this.announceCount(0, query);
    }

    buildOption(item, meta, index) {
      if (!item || !item.url) return null;

      var link = element('a', 'predictive__option predictive__option--' + meta.type);
      link.href = item.url;
      link.id = this.uid + '-option-' + index;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.setAttribute('tabindex', '-1');

      var imageUrl = '';
      if (item.featured_image && item.featured_image.url) imageUrl = item.featured_image.url;
      else if (typeof item.image === 'string') imageUrl = item.image;
      else if (item.image && item.image.url) imageUrl = item.image.url;

      if (imageUrl) {
        var thumb = element('span', 'predictive__thumb');
        var img = doc.createElement('img');
        img.src = thumbUrl(imageUrl, 120);
        img.alt = '';
        img.width = 60;
        img.height = 60;
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
        link.appendChild(thumb);
      }

      var body = element('span', 'predictive__body');
      var label = item.title || item.text || item.handle || '';
      body.appendChild(element('span', 'predictive__title', label));

      if (meta.type === 'product' && item.vendor) {
        body.appendChild(element('span', 'predictive__sub', item.vendor));
      }
      link.appendChild(body);

      if (meta.type === 'product') {
        var priceValue = item.price !== undefined ? item.price : item.price_min;
        if (priceValue !== undefined && priceValue !== null && priceValue !== '') {
          var price = element('span', 'predictive__price');
          price.appendChild(element('span', null, money(priceValue)));

          var compare = item.compare_at_price_max || item.compare_at_price_min;
          if (compare && amount(compare) > amount(priceValue)) {
            price.appendChild(element('s', null, money(compare)));
          }
          link.appendChild(price);
        }
      }

      return link;
    }

    appendViewAll(host, query) {
      if (!host) return;
      var link = element('a', 'predictive__view-all');
      link.href = searchUrl(query);
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.setAttribute('tabindex', '-1');
      link.appendChild(
        element('span', null, string('search', 'viewAll', 'View all results'))
      );
      host.appendChild(link);
    }

    /** Re-read the options after any render, section mode included. */
    collectOptions() {
      if (!this.panel) {
        this.options = [];
        return;
      }
      var found = this.panel.querySelectorAll('[role="option"]');
      this.options = Array.prototype.slice.call(found);

      for (var i = 0; i < this.options.length; i += 1) {
        var option = this.options[i];
        if (!option.id) option.id = this.uid + '-option-' + i;
        if (!option.hasAttribute('aria-selected')) option.setAttribute('aria-selected', 'false');
        option.setAttribute('tabindex', '-1');
      }

      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    announceCount(count, query) {
      var message;
      if (count === 0) {
        message = string('search', 'noResults', 'No results found');
      } else {
        message =
          String(count) + ' — ' + string('search', 'resultsFor', 'Results for') + ' “' + query + '”';
      }
      this.setStatus(message);
      announce(message);
    }

    /* ---- Recent searches ---------------------------------------------------- */

    renderRecent() {
      if (!this.recentHost) return;

      var list = readRecent();
      empty(this.recentHost);

      if (!list.length) {
        this.recentHost.setAttribute('hidden', '');
        return;
      }

      var self = this;
      var group = element('div', 'predictive__group');

      var heading = element('p', 'predictive__group-title');
      heading.appendChild(element('span', null, this.recentLabel));

      var clear = element('button', 'predictive__clear', this.clearLabel);
      clear.type = 'button';
      clear.addEventListener('click', function () {
        forgetAll();
        self.renderRecent();
        if (self.input) self.input.focus();
      });
      heading.appendChild(clear);
      group.appendChild(heading);

      var chips = element('ul', 'predictive__chips');
      for (var i = 0; i < list.length; i += 1) {
        var item = doc.createElement('li');
        var chip = element('a', 'chip chip--line predictive__chip', list[i]);
        chip.href = searchUrl(list[i]);
        item.appendChild(chip);
        chips.appendChild(item);
      }
      group.appendChild(chips);

      this.recentHost.appendChild(group);
      this.recentHost.removeAttribute('hidden');
    }
  }

  if (win.customElements && !win.customElements.get('predictive-search')) {
    win.customElements.define('predictive-search', PredictiveSearch);
  }
})();
